import {
  createClientSharedComposable,
  getStore,
  useLocalStorageObject,
} from '@global-store/store'
import { useCartStore } from '@global-store/stores/cart/cart'
import { useCheckoutResponseStore } from '@global-store/stores/checkout/response'
import type {
  CheckoutAddressInterface,
  CheckoutAddressStore,
} from '@global-store/stores/checkout/types'
import { useCustomerStore } from '@global-store/stores/customer/customer'
import { defineStore } from 'pinia'

import { isSSR } from '@fond-of/env'
import type { AddressInterface } from '@fond-of/spryker-api'

const defaultAddressValues = {
  deliveryToPackingStation: false,
  useBillingAddressForPackingStation: false,
  shippingEqualsBillingAddress: true,
}

export const useCheckoutAddressStore = createClientSharedComposable(() =>
  defineStore('checkoutAddress', {
    state: (): CheckoutAddressStore => ({
      address: useLocalStorageObject<CheckoutAddressInterface>(
        'checkoutAddress',
        defaultAddressValues
      ),
    }),

    getters: {
      deliveryToPackingStation: (state) => {
        return state.address.deliveryToPackingStation
      },
      useBillingAddressForPackingStation: (state) => {
        return state.address.useBillingAddressForPackingStation
      },
      shippingEqualsBillingAddress: (state) => {
        const customerStore = useCustomerStore()
        const billingAddressGuess =
          state.address.billingAddress || customerStore.defaultBillingAddress
        const shippingAddressGuess =
          state.address.shippingAddress || customerStore.defaultShippingAddress

        if (shippingAddressGuess?.id && billingAddressGuess?.id) {
          return shippingAddressGuess.id === billingAddressGuess.id
        }

        return state.address.shippingEqualsBillingAddress
      },

      billingAddress: (state) => {
        return state.address.billingAddress
      },
      billingAddressGuess: (state) => {
        const customerStore = useCustomerStore()
        const billingAddressGuess =
          state.address.billingAddress || customerStore.defaultBillingAddress

        if (billingAddressGuess && customerStore.customer?.email) {
          return {
            ...billingAddressGuess,
            email: customerStore.customer.email,
          } as AddressInterface
        }

        return billingAddressGuess
      },

      shippingAddress: (state) => {
        return state.address.shippingAddress
      },
      shippingAddressGuess: (state) => {
        const customerStore = useCustomerStore()

        return (
          state.address.shippingAddress || customerStore.defaultShippingAddress
        )
      },
      hideShippingAddress: () => {
        const cartStore = useCartStore()

        return cartStore.containsOnlyVouchers
      },
      displayedShippingAddress: (state) => {
        const cartStore = useCartStore()

        if (cartStore.containsOnlyVouchers) {
          return { email: state.address.shippingAddress?.email }
        }

        if (cartStore.containsVouchers) {
          return state.address.shippingAddress
        }

        return {
          ...state.address.shippingAddress,
          email: undefined,
        }
      },
    },

    actions: {
      setDeliveryToPackingStation(deliveryToPackingStation: boolean) {
        this.address.deliveryToPackingStation = deliveryToPackingStation
      },
      toggleDeliveryToPackingStation() {
        this.address.deliveryToPackingStation =
          !this.address.deliveryToPackingStation
      },

      setUseBillingAddressForPackingStation(
        useBillingAddressForPackingStation: boolean
      ) {
        this.address.useBillingAddressForPackingStation =
          useBillingAddressForPackingStation
      },

      async updateBillingAddress(
        billingAddress: AddressInterface
      ): Promise<AddressInterface> {
        const customerStore = useCustomerStore()
        const checkoutResponseStore = useCheckoutResponseStore()
        const cartStore = useCartStore()

        const isDefaultShippingAndBilling =
          billingAddress.isDefaultBilling && billingAddress.isDefaultShipping

        if (billingAddress.id && customerStore.isAuthenticated) {
          billingAddress = await customerStore.updateAddress({
            ...billingAddress,
            fkCountry: checkoutResponseStore.getCountryByIso2Code(
              billingAddress.iso2Code
            ).idCountry,
          })
        }

        this.setBillingAddress(billingAddress)

        if (
          customerStore.isAuthenticated
            ? isDefaultShippingAndBilling
            : this.address.shippingEqualsBillingAddress
        ) {
          this.setShippingAddress(billingAddress)
        }

        const iso2Code = isDefaultShippingAndBilling
          ? billingAddress.iso2Code
          : undefined

        if (cartStore.cart) {
          await checkoutResponseStore.updateCheckoutResponse(
            cartStore.cart,
            iso2Code
          )
        }

        return billingAddress
      },
      setBillingAddress(billingAddress?: AddressInterface) {
        this.address.billingAddress = billingAddress
      },

      async updateShippingAddress(shippingAddress: AddressInterface) {
        const customerStore = useCustomerStore()
        const checkoutResponseStore = useCheckoutResponseStore()
        const cartStore = useCartStore()

        const isDefaultShippingAndBilling =
          shippingAddress.isDefaultBilling && shippingAddress.isDefaultShipping

        if (
          shippingAddress.id &&
          customerStore.isAuthenticated &&
          !isDefaultShippingAndBilling
        ) {
          shippingAddress = await customerStore.updateAddress({
            ...shippingAddress,
            fkCountry: checkoutResponseStore.getCountryByIso2Code(
              shippingAddress.iso2Code
            ).idCountry,
          })
        }

        this.address.shippingAddress = {
          ...shippingAddress,
          id: isDefaultShippingAndBilling ? undefined : shippingAddress?.id,
        }

        if (this.address.billingAddress) {
          this.address.billingAddress = {
            ...this.address.billingAddress,
            isDefaultShipping: false,
          }
        }

        if (cartStore.cart) {
          await checkoutResponseStore.updateCheckoutResponse(
            cartStore.cart,
            shippingAddress.iso2Code
          )
        }

        this.address.shippingEqualsBillingAddress = false

        return shippingAddress
      },
      setShippingAddress(shippingAddress?: AddressInterface) {
        this.address.shippingAddress = shippingAddress
      },

      setShippingEqualsBillingAddress(shippingEqualsBillingAddress: boolean) {
        this.address.shippingEqualsBillingAddress = shippingEqualsBillingAddress
      },

      $reset() {
        this.address = defaultAddressValues

        if (isSSR()) {
          return
        }

        localStorage.removeItem('checkoutAddress')
      },
    },

    hydrate: (storeState) => {
      // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/43826
      storeState.address = useLocalStorageObject<CheckoutAddressInterface>(
        'checkoutAddress',
        defaultAddressValues
      )
    },
  })(getStore())
)
